import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Search, Filter, Plus, Phone } from "lucide-react";
import LeadTable from "./LeadTable";
import AddLeadModal from "./AddLeadModal";
import FilterSidebar from "./FilterSidebar";
import SmartViews from "./SmartViews";
import apiService from "../../services/apiService";
import { Lead as CRMLead } from "../../types/crm";
import { CRMDashboardCount, DashboardResponse, RMList } from "../../utils/interfaces";

interface OverviewProps {
  leads: CRMLead[];
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  loading: boolean;
  rmList: RMList[];
  onLeadAdded: () => void;
  filterLead: (viewId: string) => void;
  applyfilter: (filter: any) => void;
  todayCalls: () => void;
  onSearchChange: (term: string) => void;
  onUpdateLeadsdata: (lead: CRMLead) => void;
  searchTerm: string;
  onDeleteLeads: (id: number) => void;
  leadLocation: any[];
}

const OverviewTab = ({
  leads,
  total,
  currentPage,
  pageSize,
  onPageChange,
  loading,
  rmList,
  onLeadAdded,
  filterLead,
  applyfilter,
  todayCalls,
  onSearchChange,
  searchTerm,
  onUpdateLeadsdata,
  onDeleteLeads,
  leadLocation,
}: OverviewProps) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);
  const [activeView, setActiveView] = useState("all");
  const [dashboardData, setDashboardData] = useState<CRMDashboardCount>({
    all: 0,
    interested: 0,
    meetings: 0,
    proposals: 0,
    warm: 0,
    hot: 0,
    unresponsive: 0,
    overdue: 0,
    todayScheduled: 0,
  });
  const [statusOptions, setStatusOptions] = useState<{ id: number; value: string }[]>([]);
  const hasFetched = useRef(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    getMetricsData();
  }, []);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await apiService.get<{ data: { id: number; value: string }[] }>(
          "/leads/getLeadStatuses"
        );
        setStatusOptions(response.data);
      } catch {
        setStatusOptions([]);
      }
    };

    fetchStatuses();
  }, []);

  const getMetricsData = async () => {
    try {
      const response: DashboardResponse = await apiService.get("/leads/dashboard");
      setDashboardData({
        all: response.all,
        interested: response.interested,
        meetings: response.meetings,
        proposals: response.proposals,
        warm: response.warm,
        hot: response.hot,
        unresponsive: response.unresponsive,
        overdue: response.overdue,
        todayScheduled: response.todayScheduled,
      });
    } catch {
      setDashboardData({
        all: 0,
        interested: 0,
        meetings: 0,
        proposals: 0,
        warm: 0,
        hot: 0,
        unresponsive: 0,
        overdue: 0,
        todayScheduled: 0,
      });
    }
  };

  const handleUpdateLead = (updatedLead: CRMLead) => {
    onUpdateLeadsdata(updatedLead);
    getMetricsData();
  };

  const handleDeleteLead = (id: number) => {
    onDeleteLeads(id);
    getMetricsData();
  };

  const applyFilters = (filter: any) => {
    applyfilter(filter);
    setActiveView("all");
  };

  const handlefilterLead = (viewId: string) => {
    filterLead(viewId);
    setActiveView(viewId);
  };

  return (
    <div className="space-y-3.5">
            <SmartViews
                leadCount={{
                    all: dashboardData.all ?? 0,
                    interested: dashboardData.interested ?? 0,
                    meetings: dashboardData.meetings ?? 0,
                    proposals: dashboardData.proposals ?? 0,
                    warm: dashboardData.warm ?? 0,
                    hot: dashboardData.hot ?? 0,
                    unresponsive: dashboardData.unresponsive ?? 0,
                    overdue: dashboardData.overdue ?? 0,
                    todayScheduled: dashboardData.todayScheduled ?? 0,
                }}
                onViewChange={handlefilterLead}
                showactiveview={activeView}
            />

            <Card className="rounded-[20px] border-[#eadfc0] bg-white shadow-[0_10px_20px_rgba(15,23,42,0.045)]">
                <CardContent className="p-3 md:p-3.5">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center gap-3">
                            <div
                                className={`relative w-full transition-all duration-300 lg:min-w-[360px] ${isSearchFocused ? "lg:min-w-[420px]" : ""
                                    }`}
                            >
                                <Search
                                    className={`absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 transition-all duration-300 ${isSearchFocused ? "text-[#3f8b98] scale-110" : "text-slate-400 opacity-70"
                                        }`}
                                />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search leads..."
                                    value={searchTerm}
                                    onChange={(e) => onSearchChange(e.target.value)}
                                    onFocus={() => setIsSearchFocused(true)}
                                    onBlur={() => setIsSearchFocused(false)}
                                    className={`w-full h-11 pl-11 pr-14 text-[13.5px] font-medium text-slate-700 bg-white border border-slate-200 rounded-2xl transition-all duration-300 outline-none placeholder:text-slate-400/80 ${isSearchFocused
                                            ? "border-[#3f8b98] ring-4 ring-[#3f8b98]/10"
                                            : "hover:border-slate-300"
                                        }`}
                                />
                                <div
                                    className={`absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1 px-1.5 py-0.5 rounded-lg border border-slate-200 bg-slate-50 text-[10px] font-bold text-slate-400 transition-all duration-300 pointer-events-none select-none ${isSearchFocused ? "opacity-40 scale-90" : ""
                                        }`}
                                >
                                    <span className="text-[9px] opacity-70 font-semibold">Ctrl</span> K
                                </div>
                            </div>

              <Button
                variant="outline"
                onClick={() => setShowFilters(true)}
                className="h-11 w-14 shrink-0 rounded-2xl border-[#eadfc0] bg-white px-0 text-[#8f6f3c] shadow-none hover:bg-[#fff9ef]"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={todayCalls}
                className="h-11 rounded-2xl border-[#eadfc0] bg-white px-4 text-[13px] text-[#8f6f3c] shadow-none hover:bg-[#fff9ef]"
              >
                <Phone className="mr-2 h-4 w-4" />
                Calls
                <span className="ml-2.5 rounded-full bg-red-600 px-2.5 py-0.5 text-[11px] font-semibold text-white">
                  {dashboardData.todayScheduled ?? 0}
                </span>
              </Button>

              <Button
                onClick={() => setShowAddModal(true)}
                className="h-11 rounded-2xl bg-[#3f8b98] px-5 text-[13px] font-semibold text-white shadow-[0_10px_18px_rgba(63,139,152,0.22)] hover:bg-[#4d9aa6]"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Lead
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <LeadTable
        leads={leads}
        onUpdateLeads={handleUpdateLead}
        onDeleteLeads={handleDeleteLead}
        total={total}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        loading={loading}
        rmList={rmList}
        statusOptions={statusOptions}
        onLeadAdded={() => {
          onLeadAdded();
          getMetricsData();
        }}
      />

      <AddLeadModal
        open={showAddModal}
        onOpenChange={(open) => setShowAddModal(open)}
        onAddLead={() => {
          onLeadAdded();
          getMetricsData();
        }}
        rmList={rmList}
        onLeadAdded={onLeadAdded}
        onUploadSuccess={onLeadAdded}
      />

      <FilterSidebar
        rmList={rmList}
        onApplyFilters={applyFilters}
        open={showFilters}
        onOpenChange={setShowFilters}
        leads={leads}
        onFilter={() => undefined}
        onFiltersChange={setActiveFilters}
        cleardata={false}
        leadLocation={leadLocation}
      />
    </div>
  );
};

export default OverviewTab;
